// Common metric interface, layered as layout -> pairwise intersections -> metrics.
// Runs in the browser (the tool) and headless under Node (tests, experiments).
import { computeLayout } from '../layout/layout.js';
import { correctedMetrics } from './corrected.js';
import { METRICS, VERSIONS } from './directions.js';

export const DEFAULT_VERSION = 'corrected';

function checkVersion(version) {
    if (!VERSIONS.includes(version)) {
        throw new Error('Unknown metric version: ' + version);
    }
}

/**
 * All metrics of one configuration in one call.
 * @param config         [{ name, categories }]
 * @param dataset        dataset from data/dataset-loader.js
 * @param layoutOptions  { width, height, spacing }, default 960 x 600, spacing 20
 * @param options        { version: 'corrected' } (the only version; the legacy one was removed in todo/11)
 * @returns object with the database columns (overlap, slope, ...)
 */
export function evaluateAll(config, dataset, layoutOptions = {}, { version = DEFAULT_VERSION } = {}) {
    checkVersion(version);
    const layout = computeLayout(dataset, config, layoutOptions);
    return correctedMetrics(layout, dataset);
}

/**
 * Score of one metric (a key of METRICS in directions.js) for one configuration.
 */
export function evaluate(config, metric, dataset, layoutOptions = {}, { version = DEFAULT_VERSION } = {}) {
    if (!METRICS[metric]) {
        throw new Error('Unknown metric: ' + metric);
    }
    checkVersion(version);
    return evaluateAll(config, dataset, layoutOptions, { version })[METRICS[metric][version].column];
}

/**
 * Superseded: a move changes the stacking offsets of whole subtrees, so there is no cheaper delta than the full
 * structural evaluation (StructuralEvaluator in structural.js, 1 to 40 us). Kept so that old callers fail loudly.
 */
export function evaluateDelta(config, move, metric, cachedState) {
    throw new Error('evaluateDelta is not implemented; use StructuralEvaluator in src/metrics/structural.js');
}
